import { graphemes } from '@/common/data/scopeAndSequence.json';
import { WordId } from '@/common/types/scopeAndSequence';
import { getWordById, getWordGraphemes } from '@/common/utils/scopeAndSequence';
import { segmentTypedAnswer } from '@/common/utils/segmentTypedAnswer';
import type { Attempt } from '../types/session';

export interface GraphemeStats {
  totalAttempts: number;
  totalCorrect: number;
  isolatedCount: number;
  isolatedCorrect: number;
  inWordCount: number;
  inWordCorrect: number;
  recentWords: string[]; // newest first, unique, up to 3
}

export type GraphemeStatsMap = Map<string, GraphemeStats>;

export function buildGraphemeStats(attempts: Attempt[]): GraphemeStatsMap {
  const MAX_RECENT_WORDS = 5;

  const allIds = graphemes.map((g) => g.id);
  const targetIds = new Set(allIds);

  // Pre-seed stats for all graphemes
  const stats: GraphemeStatsMap = new Map(
    allIds.map((id) => [
      id,
      {
        totalAttempts: 0,
        totalCorrect: 0,
        isolatedCount: 0,
        isolatedCorrect: 0,
        inWordCount: 0,
        inWordCorrect: 0,
        recentWords: [],
      },
    ]),
  );

  // Temp store of word events per gid for correct “newest unique” selection
  const wordEventsByGid = new Map<
    string,
    { wordId: string; startedAt: number }[]
  >(allIds.map((id) => [id, []]));

  // Cache for word lookups
  type WordData = {
    spelling: string;
    graphemeIds: string[];
    graphemeSpellings: string[];
  };
  const wordCache = new Map<string, WordData>();
  const getWordData = (wordId: string): WordData | null => {
    const cached = wordCache.get(wordId);
    if (cached) return cached;
    const w = getWordById(wordId as WordId);
    if (!w) return null;
    const wgs = getWordGraphemes(w);
    const data: WordData = {
      spelling: w.spelling,
      graphemeIds: wgs.map((g) => g.id),
      graphemeSpellings: wgs.map((g) => g.spelling),
    };
    wordCache.set(wordId, data);
    return data;
  };

  const ensure = (gid: string): GraphemeStats => {
    let s = stats.get(gid);
    if (!s) {
      s = {
        totalAttempts: 0,
        totalCorrect: 0,
        isolatedCount: 0,
        isolatedCorrect: 0,
        inWordCount: 0,
        inWordCorrect: 0,
        recentWords: [],
      };
      stats.set(gid, s);
      wordEventsByGid.set(gid, []);
    }
    return s;
  };

  for (const a of attempts) {
    const id = a.correctAnswerId;
    if (!id) continue;

    if (id.startsWith('grapheme-')) {
      const gid = id;
      const s = ensure(gid);
      const isCorrect =
        (a.studentAnswer ?? '').toLowerCase() ===
        (a.correctAnswer ?? '').toLowerCase();
      s.totalAttempts += 1;
      if (isCorrect) s.totalCorrect += 1;
      s.isolatedCount += 1;
      if (isCorrect) s.isolatedCorrect += 1;
      continue;
    }

    if (id.startsWith('word-')) {
      const wordId = id;
      const w = getWordData(wordId);
      if (!w) continue;

      const attemptGraphemes = segmentTypedAnswer(
        a.studentAnswer,
        w.graphemeSpellings,
      );

      for (let i = 0; i < w.graphemeIds.length; i++) {
        const gid = w.graphemeIds[i];
        const s = ensure(gid);
        const isCorrect =
          (attemptGraphemes[i] ?? '').toLowerCase() ===
          (w.graphemeSpellings[i] ?? '').toLowerCase();

        s.totalAttempts += 1;
        if (isCorrect) s.totalCorrect += 1;

        if (targetIds.has(gid)) {
          s.inWordCount += 1;
          if (isCorrect) s.inWordCorrect += 1;
          wordEventsByGid.get(gid)!.push({ wordId, startedAt: a.startedAt });
        }
      }
    }
  }

  // Finalize recentWords per grapheme (newest unique, up to MAX_RECENT_WORDS)
  for (const [gid, events] of wordEventsByGid) {
    if (!events.length) continue;
    events.sort((a, b) => b.startedAt - a.startedAt);
    const seen = new Set<string>();
    const recentWordIds: string[] = [];
    for (const e of events) {
      if (seen.has(e.wordId)) continue;
      seen.add(e.wordId);
      recentWordIds.push(e.wordId);
      if (recentWordIds.length >= MAX_RECENT_WORDS) break;
    }
    const recentWords = recentWordIds
      .map(
        (wid) =>
          wordCache.get(wid)?.spelling ?? getWordById(wid as WordId)?.spelling,
      )
      .filter(Boolean) as string[];
    const s = ensure(gid);
    s.recentWords = recentWords;
  }

  return stats;
}
